import React, {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useState
} from 'react';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { useHotkeys } from 'react-hotkeys-hook';
import { useParams } from 'react-router-dom';
import { Modal, Paper } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { generateFileUrl, getFileNameFromUrl } from '@utils/common';
import { translate } from '@utils/locale';
import { colors } from '@constants/colors';
import {
  IMAGE_FORMATS,
  LONG_FIELD_MAX_LENGTH,
  RegularExpressions,
  TEXT_FIELD_MAX_LENGTH
} from '@constants/common';
import {
  DualButton,
  HorizontalStepper,
  ImagePreviewCard,
  RhfDropdown,
  RhfTextField
} from '@components';
import { ToastTypes } from '@type/toast';
import { GenericApiResponse } from '@type/general';
import { showGenericErrorToast } from '@utils/error';
import ImageUploader from '@containers/ImageUploader/ImageUploader';
import displayToast from '@components/toast/customToast';

import {
  AddTeamMembersModalPropsType,
  CreateTeamFormFieldValues
} from '../types';
import {
  useCreateTeamMemberMutation,
  useUpdateTeamMemberMutation
} from '../api';
import { FIELD_TO_TAB_MAPPER_TEAM_MEMBER } from '../constants';
import { formatCreateUpdateTeamMemberPayload } from '../services/teamMember';

const AddTeamMembersModal: FC<AddTeamMembersModalPropsType> = ({
  isOpen,
  handleCloseModal,
  memberDetails,
  resetOffset,
  maxOrder
}) => {
  const [deleteClickedFile, setDeleteClickedFile] = useState<string>('');
  const [activeStep, setActiveStep] = useState<number>(0);
  const [isSaveButtonClicked, setIsSaveButtonClicked] = useState(false);

  const [createTeamMember, { isLoading }] = useCreateTeamMemberMutation();
  const [updateTeamMember, { isLoading: isTeamMemberUpdating }] =
    useUpdateTeamMemberMutation();

  const { companyId } = useParams();

  const schema = yup.object().shape({
    teamMemberName: yup
      .string()
      .required(translate('company.placeholders.teamMemberName'))
      .test(
        'required',
        translate('company.placeholders.teamMemberName'),
        val => !!val.trimEnd()
      ),
    teamMemberEmail: yup
      .string()
      .email(translate('company.placeholders.teamMemberEmail'))
      .matches(
        RegularExpressions.optionalEmailRegex,
        translate('company.placeholders.teamMemberEmail')
      )
      .nullable(),
    teamMemberSecondaryEmail: yup
      .string()
      .email(translate('company.placeholders.teamMemberSecondaryEmail'))
      .matches(
        RegularExpressions.optionalEmailRegex,
        translate('company.placeholders.teamMemberSecondaryEmail')
      )
      .nullable(),
    teamMemberLinkedInUrl: yup
      .string()
      .url('Invalid Url')
      .required(translate('company.placeholders.teamMemberLinkedin'))
      .test(
        translate('company.placeholders.teamMemberLinkedin'),
        val => !!val.trimEnd()
      ),
    teamMemberDesignation: yup.string().nullable(),
    teamMemberDescription: yup
      .string()
      .required(translate('company.placeholders.teamMemberBio'))
      .test(
        'required',
        translate('company.placeholders.teamMemberBio'),
        val => !!val.trimEnd()
      ),
    teamMemberRole: yup
      .string()
      .required(translate('required'))
      .test('required', translate('required'), val => !!val.trimEnd()),
    // teamMemberCoverImage: !isSaveButtonClicked
    //   ? yup.mixed()
    //   : yup
    //       .mixed()
    //       .test(
    //         'requiredValidation',
    //         translate('company.placeholders.teamMemberCoverImage'),
    //         (value: { file: File }) => {
    //           return !!value?.file;
    //         }
    //       ),
    teamMemberProfileImage: !isSaveButtonClicked
      ? yup.mixed()
      : yup
          .mixed()
          .test(
            'requiredValidation',
            translate('company.placeholders.teamMemberProfileImage'),
            (value: { file: File }) => {
              return !!value?.file;
            }
          )
  });

  const methods = useForm<CreateTeamFormFieldValues>({
    resolver: yupResolver(
      schema
    ) as never as Resolver<CreateTeamFormFieldValues>,
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  const {
    control,
    reset,
    getValues,
    setValue,
    clearErrors,
    trigger,
    handleSubmit,
    formState: { errors }
  } = methods;

  const showProfileImageUploader =
    deleteClickedFile === 'teamMemberProfileImage' ||
    !getValues('teamMemberProfileImage.path');

  // const showCoverImageUploader =
  //   deleteClickedFile === 'teamMemberCoverImage' ||
  //   !getValues('teamMemberCoverImage.path');

  const steps = ['Member Info', 'Upload Photos'];

  const teamMemberRole = [
    {
      id: 'founder',
      name: 'Founder'
    },
    {
      id: 'company-executive',
      name: 'Executives'
    },
    {
      id: 'board-of-directors',
      name: 'Board of Directors'
    },
    {
      id: 'other',
      name: 'Other'
    }
  ];

  const deleteFile = (value: string) => {
    setDeleteClickedFile(value);
    setValue(value as never as keyof CreateTeamFormFieldValues, {
      file: undefined,
      path: ''
    });
  };

  const onFileUploadSuccess = (
    file: File,
    path: string,
    name: keyof CreateTeamFormFieldValues
  ) => {
    setValue(name, {
      file: file,
      path: path
    });
    clearErrors(name);
    setDeleteClickedFile('');
  };

  const onClickCancel = () => {
    reset({});
    handleCloseModal();
  };

  const getImageUrl = (name: string) =>
    generateFileUrl(
      getValues(
        `${name}.path` as never as keyof CreateTeamFormFieldValues
      ) as string,
      getValues(
        `${name}.file` as never as keyof CreateTeamFormFieldValues
      ) as unknown as File
    );

  const getFileName = (value: string) =>
    (
      getValues(
        `${value}.file` as never as keyof CreateTeamFormFieldValues
      ) as unknown as File
    )?.name;

  const stepClickHandler = async (index: number) => {
    const isValid = await trigger(
      FIELD_TO_TAB_MAPPER_TEAM_MEMBER[
        activeStep as keyof typeof FIELD_TO_TAB_MAPPER_TEAM_MEMBER
      ] as never as keyof CreateTeamFormFieldValues
    );

    if (isValid) {
      if (index < steps.length - 1) {
        setActiveStep(index + 1);
      } else {
        setIsSaveButtonClicked(true);
        return true;
      }
    } else {
      setActiveStep(activeStep);
    }
    return false;
  };

  const handleStepClick = (index: number) => {
    stepClickHandler(index - 1);
  };

  const onClickPrimaryButton = async () => {
    const saveClicked = await stepClickHandler(activeStep);
    return saveClicked;
  };

  const onClickSecondaryButton = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
    else onClickCancel();
  };

  const onChangeFieldValue = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    name: keyof CreateTeamFormFieldValues
  ) => {
    setValue(name, event.target.value);
    clearErrors(name);
  };

  const onChangeDropdownValue = (
    selectedOption: string,
    name: keyof CreateTeamFormFieldValues
  ) => {
    setValue(name, selectedOption);
    clearErrors(name);
  };

  const handleCreateUpdateRes = (response: GenericApiResponse<string>) => {
    if (response.result === 'SUCCESS') {
      setIsSaveButtonClicked(false);
      setActiveStep(0);
      handleCloseModal();
      resetOffset();
    } else {
      displayToast(
        ToastTypes.ERROR,
        response.error?.details?.[0] ?? 'Team member creation failed'
      );
    }
  };

  const onSubmit = useCallback(
    async (data: CreateTeamFormFieldValues, saveClicked?: boolean) => {
      if (saveClicked || isSaveButtonClicked) {
        try {
          if (memberDetails?.id) {
            const response = await updateTeamMember({
              company_id: companyId ?? '',
              payload: formatCreateUpdateTeamMemberPayload(data),
              member_id: memberDetails.id
            }).unwrap();
            handleCreateUpdateRes(response);
          } else {
            const response = await createTeamMember({
              company_id: companyId ?? '',
              payload: formatCreateUpdateTeamMemberPayload({
                ...data,
                teamMemberOrder: maxOrder + 1
              })
            }).unwrap();
            handleCreateUpdateRes(response);
          }
        } catch (err) {
          showGenericErrorToast(err);
        }
      }
    },
    [isSaveButtonClicked]
  );

  useHotkeys(
    'enter',
    async () => {
      const saveClicked = await onClickPrimaryButton();
      handleSubmit(data => onSubmit(data, saveClicked))();
    },
    [isSaveButtonClicked, activeStep, onSubmit, onClickPrimaryButton],
    { enabled: isOpen }
  );

  const renderForm = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <RhfTextField
              name="teamMemberName"
              label={translate('company.addTeamMember.name')}
              placeholder={translate('company.placeholders.teamMemberName')}
              control={control}
              errors={errors}
              onChange={event => onChangeFieldValue(event, 'teamMemberName')}
              wrapperClass="mb-5 w-full"
              maxLength={TEXT_FIELD_MAX_LENGTH}
              showCharacterLimit
              autoFocusField={true}
            />
            <RhfTextField
              name="teamMemberEmail"
              label={translate('company.addTeamMember.email')}
              placeholder={translate('company.placeholders.teamMemberEmail')}
              onChange={event => onChangeFieldValue(event, 'teamMemberEmail')}
              control={control}
              errors={errors}
              wrapperClass="mb-5 w-full"
              showCharacterLimit
            />
            <RhfTextField
              name="teamMemberSecondaryEmail"
              label={translate('company.addTeamMember.secEmail')}
              placeholder={translate(
                'company.placeholders.teamMemberSecondaryEmail'
              )}
              onChange={event =>
                onChangeFieldValue(event, 'teamMemberSecondaryEmail')
              }
              control={control}
              errors={errors}
              wrapperClass="mb-5 w-full"
              showCharacterLimit
            />
            <RhfTextField
              name="teamMemberDesignation"
              label={translate('company.addTeamMember.designation')}
              placeholder={translate(
                'company.placeholders.teamMemberDesignation'
              )}
              onChange={event =>
                onChangeFieldValue(event, 'teamMemberDesignation')
              }
              control={control}
              errors={errors}
              wrapperClass="mb-5 w-full"
              maxLength={TEXT_FIELD_MAX_LENGTH}
              showCharacterLimit
              description={translate(
                'company.description.teamMemberDesignation'
              )}
            />
            <RhfDropdown
              name="teamMemberRole"
              control={control}
              placeholder={translate('company.addTeamMember.role')}
              onChangeHandler={value =>
                onChangeDropdownValue(value, 'teamMemberRole')
              }
              dropdownLOV={teamMemberRole}
              selectedItem={getValues('teamMemberRole')}
              errors={errors}
              wrapperClass="mb-5 w-full"
              description={translate('company.description.teamMemberRole')}
            />
            <RhfTextField
              name="teamMemberLinkedInUrl"
              label={translate('company.addTeamMember.linkedInURL')}
              placeholder={translate('company.placeholders.teamMemberLinkedin')}
              onChange={event =>
                onChangeFieldValue(event, 'teamMemberLinkedInUrl')
              }
              control={control}
              errors={errors}
              wrapperClass="mb-5 w-full"
              description={translate(
                'company.description.teamMemberLinkedInUrl'
              )}
              showCharacterLimit
            />
            <RhfTextField
              name="teamMemberDescription"
              label={translate('company.addTeamMember.description')}
              placeholder={translate('company.placeholders.teamMemberBio')}
              onChange={event =>
                onChangeFieldValue(event, 'teamMemberDescription')
              }
              control={control}
              multiline={true}
              errors={errors}
              rows={3}
              wrapperClass="mb-5 w-full"
              maxLength={LONG_FIELD_MAX_LENGTH}
              description={translate(
                'company.description.teamMemberDescription'
              )}
              showCharacterLimit
            />
          </>
        );
      case 1:
        return (
          <>
            <div className="flex flex-row items-center pb-2 mt-5 text-base font-normal text-black/60">
              {translate('company.addTeamMember.logoDescription')}
            </div>
            <div className="flex flex-row items-center pb-2 text-base font-normal text-black/60">
              {translate('company.addTeamMember.profileImage')}
            </div>
            {showProfileImageUploader ? (
              <ImageUploader
                name="teamMemberProfileImage"
                enableCrop={true}
                sizeInMb={3}
                supportedFormats={IMAGE_FORMATS}
                fileUploadSuccessHandler={(file, path) =>
                  onFileUploadSuccess(file, path, 'teamMemberProfileImage')
                }
                deleteFileHandler={() => deleteFile('teamMemberProfileImage')}
                errors={errors}
                previousFile={getValues('teamMemberProfileImage.file')}
                aspectRatio={1}
                showPreview={false}
                customUploadText={translate('company.uploadImageText', {
                  aspectRatio: '1:1'
                })}
              />
            ) : (
              <ImagePreviewCard
                name="teamMemberProfileImage"
                isEdit={true}
                handleDeleteClick={() => deleteFile('teamMemberProfileImage')}
                imageUrl={getImageUrl('teamMemberProfileImage')}
                fileName={getFileName('teamMemberProfileImage')}
              />
            )}
            {/* <div className="flex flex-row items-center pb-2 mt-5 text-base font-normal text-black/60">
              {translate('company.addTeamMember.coverImage')}
            </div> */}
            {/* {showCoverImageUploader ? (
              <ImageUploader
                name="teamMemberCoverImage"
                enableCrop={true}
                sizeInMb={3}
                supportedFormats={IMAGE_FORMATS}
                fileUploadSuccessHandler={(file, path) =>
                  onFileUploadSuccess(file, path, 'teamMemberCoverImage')
                }
                deleteFileHandler={() => deleteFile('teamMemberCoverImage')}
                errors={errors}
                previousFile={getValues('teamMemberCoverImage.file')}
                aspectRatio={2}
                showPreview={true}
                customUploadText={translate('company.uploadImageText', {
                  aspectRatio: '2:1'
                })}
              />
            ) : (
              <ImagePreviewCard
                name="teamMemberCoverImage"
                isEdit={true}
                handleDeleteClick={() => deleteFile('teamMemberCoverImage')}
                imageUrl={getImageUrl('teamMemberCoverImage')}
                fileName={getFileName('teamMemberCoverImage')}
              />
            )} */}
          </>
        );
      default:
        return <></>;
    }
  };

  useEffect(() => {
    if (memberDetails?.id) {
      reset({
        teamMemberCoverImage: {
          file: memberDetails.coverImagePath
            ? new File([], getFileNameFromUrl(memberDetails.coverImagePath))
            : undefined,
          path: memberDetails.coverImagePath
        },
        teamMemberDescription: memberDetails.description,
        teamMemberDesignation: memberDetails.designation || null,
        teamMemberEmail: memberDetails.email || null,
        teamMemberLinkedInUrl: memberDetails.linkedinUrl,
        teamMemberName: memberDetails.name,
        teamMemberProfileImage: {
          file: memberDetails.profileImagePath
            ? new File([], getFileNameFromUrl(memberDetails.profileImagePath))
            : undefined,
          path: memberDetails.profileImagePath
        },
        teamMemberRole: [
          'founder',
          'company-executive',
          'board-of-directors',
          'other'
        ].includes(memberDetails.role)
          ? memberDetails.role
          : '',
        teamMemberSecondaryEmail: memberDetails.secondaryEmail || null,
        teamMemberOrder: memberDetails.order ?? 0
      });
    } else {
      reset({});
    }
  }, [memberDetails, isOpen]);

  return (
    <div>
      <Modal open={isOpen} onClose={handleCloseModal}>
        <div className="flex flex-col justify-center items-center h-full bg-black/60">
          <Paper
            style={{
              paddingTop: 20,
              paddingBottom: 20,
              width: 450,
              height: 744,
              overflow: 'auto'
            }}>
            <div className="flex flex-col bg-white">
              <div className="px-5 pb-5 mb-5 text-lg font-medium leading-6 border-b border-gray-200 ">
                {translate('company.addTeamMember.teamMembers')}
              </div>
              <div className="px-5">
                <HorizontalStepper
                  activeStep={activeStep}
                  handleStep={handleStepClick}
                  steps={steps}
                />
              </div>
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(data => onSubmit(data))}>
                  <div className="p-5 w-full min-h-[550px]">{renderForm()}</div>
                  <div className="flex justify-end px-5 pt-5 text-lg font-medium leading-6 border-t border-gray-200">
                    <DualButton
                      primaryButtonLabel={translate(
                        activeStep == 0
                          ? 'company.addTeamMember.next'
                          : 'company.addTeamMember.save'
                      )}
                      secondaryButtonVariant="text"
                      secondaryButtonLabel={translate(
                        activeStep == 0
                          ? 'company.addTeamMember.cancel'
                          : 'company.addTeamMember.back'
                      )}
                      secondaryButtonCustomStyle={{
                        color: colors.primaryBlue
                      }}
                      onChangePrimaryButton={onClickPrimaryButton}
                      onChangeSecondaryButton={onClickSecondaryButton}
                      primaryButtonType="submit"
                      isPrimaryButtonLoading={isLoading || isTeamMemberUpdating}
                    />
                  </div>
                </form>
              </FormProvider>
            </div>
          </Paper>
        </div>
      </Modal>
    </div>
  );
};

export default AddTeamMembersModal;
